import { useToast } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { useState } from "react";
import { useIntl } from "react-intl";

import { AsyncSubmitFunction } from "@nens/ranalib";
import { Schematisation } from "@nens/threedi/openapi-client";

import { useUpdateSchematisationMutation } from "@/lib/schematisation-details/api/hcc-schematisation";
import { useUpdateRevisionMutation } from "@/lib/schematisation-details/api/hcc-schematisation-revision";

import { useSchematisationDetailsContext } from "../schematisation-details-context";

export type MetadataFormValues = {
  description: string;
  commitMessage: string;
};

export function useModelSchematisationMetadataSubmit() {
  const { schematisation, revision } = useSchematisationDetailsContext();
  const schematisationDescription = schematisation.meta?.description ?? "";
  const commitMessage = revision.commitMessage ?? "";

  const intl = useIntl();
  const toast = useToast();

  // There is no array input field in our form library, so we need to manage the tags state ourselves.
  const [tags, setTags] = useState<string[]>(schematisation.tags ?? []);

  const { mutateAsync: updateRevision, isMutating: isMutatingRevision } = useUpdateRevisionMutation(
    revision.id,
    schematisation.id
  );

  const { mutateAsync: updateSchematisationMutation, isMutating: isMutatingSchematisation } =
    useUpdateSchematisationMutation(schematisation.id);

  const isMutating = isMutatingRevision || isMutatingSchematisation;

  const submit: AsyncSubmitFunction<MetadataFormValues> = async ({
    description,
    commitMessage: newCommitMessage,
  }) => {
    const updates: Promise<unknown>[] = [];

    // Update schematisation metadata if it has changed
    const schematisationUpdate: Partial<Schematisation> = {};
    if (description !== schematisationDescription) {
      schematisationUpdate.meta = { description };
    }
    if (!isEqual(tags, schematisation.tags ?? [])) {
      schematisationUpdate.tags = tags;
    }
    if (Object.keys(schematisationUpdate).length > 0) {
      updates.push(updateSchematisationMutation(schematisationUpdate));
    }

    if (newCommitMessage !== commitMessage) {
      updates.push(updateRevision({ commitMessage: newCommitMessage }));
    }
    if (!updates.length) {
      return;
    }

    try {
      await Promise.all(updates);
    } catch {
      // The mutations throw there own toasts, so we don't need to show another one here.
      return;
    }

    toast({
      status: "success",
      title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
      description: intl.formatMessage({
        id: "file.schematisation.metadata_update_success",
        defaultMessage: "Model schematisation metadata has been updated",
      }),
    });
  };

  return { submit, isMutating, tags, setTags };
}
